import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ApplicationRole,
  ManagedUserResponse,
  UsersService,
} from "../../../api";
import { TabsHelpers } from "../../../components/basic/TabsHelpers";
import { isUserActive, toggleUserActive } from "./userActiveActions";

const MySwal = withReactContent(Swal);
const COLUMN_COUNT = 6;
const DEFAULT_ROWS_PER_PAGE = 25;

const ROLE_LABELS: Record<string, string> = {
  [ApplicationRole.ADMIN]: "Admin",
  [ApplicationRole.ATTORNEY]: "Attorney",
  [ApplicationRole.STAFF]: "Staff",
  [ApplicationRole.STAFF_ATTORNEY]: "Staff Attorney",
  [ApplicationRole.USER]: "User",
  [ApplicationRole.REFFERAL_STAFF]: "Referral Staff",
  [ApplicationRole.CLIO_STAFF]: "Clio Staff",
  [ApplicationRole.CLIO_ADMIN]: "Clio Admin",
  [ApplicationRole.CLIO_MANAGER]: "Clio Manager",
  [ApplicationRole.CLIO_TASK_ASSIGNER]: "Clio Task Assigner",
  [ApplicationRole.QA]: "QA",
};

function formatRoles(roles?: ApplicationRole[] | null) {
  if (!roles?.length) return "—";
  return roles.map((role) => ROLE_LABELS[role] ?? role).join(", ");
}

function matchesSearch(user: ManagedUserResponse, searchTerm: string) {
  const lower = searchTerm.toLowerCase();
  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase();
  const rolesText = formatRoles(user.roles).toLowerCase();

  return (
    fullName.includes(lower) ||
    (user.email?.toLowerCase().includes(lower) ?? false) ||
    rolesText.includes(lower)
  );
}

export default function StaffUsersTable() {
  const [value, setValue] = useState(0);
  const [users, setUsers] = useState<ManagedUserResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const navigate = useNavigate();

  const loadUsers = useCallback(() => {
    setLoading(true);
    UsersService.postApiUsersGetManagedUsers({ includeInactive: true })
      .then(setUsers)
      .catch(() => {
        setUsers([]);
        MySwal.fire({
          icon: "error",
          title: <p>Could not load users.</p>,
          text: "If this is a new deployment, make sure the database migration for user deactivation has been applied.",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const activeUsers = useMemo(
    () => users.filter((user) => isUserActive(user)),
    [users]
  );
  const inactiveUsers = useMemo(
    () => users.filter((user) => !isUserActive(user)),
    [users]
  );

  const tabUsers = value === 0 ? activeUsers : inactiveUsers;

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return tabUsers;
    }
    return tabUsers.filter((user) => matchesSearch(user, searchTerm.trim()));
  }, [tabUsers, searchTerm]);

  const paginatedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const handleToggleActive = async (user: ManagedUserResponse, isActive: boolean) => {
    await toggleUserActive(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      isActive,
      onSuccess: loadUsers,
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage user access without deleting accounts.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => navigate("/generateLink?setAny=true")}>
          Create registration link
        </Button>
      </Box>

      <Tabs
        value={value}
        onChange={(_, newValue) => {
          setValue(newValue);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      >
        <Tab label={`ACTIVE (${activeUsers.length})`} {...TabsHelpers.parseProps(0)} />
        <Tab label={`INACTIVE (${inactiveUsers.length})`} {...TabsHelpers.parseProps(1)} />
      </Tabs>

      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, bgcolor: "grey.50" }}>
        <TextField
          placeholder="Search by name, email, or role..."
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: "100%", sm: 360 } }}
        />
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {!loading && filteredUsers.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: "block" }}>
            {filteredUsers.length} user{filteredUsers.length === 1 ? "" : "s"}
          </Typography>
        )}

        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56, minWidth: 56 }}>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6 }}>
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No users match the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user, index) => (
                  <TableRow key={user.id}>
                    <TableCell sx={{ color: "text.secondary", fontWeight: 500 }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{formatRoles(user.roles)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={isUserActive(user) ? "Active" : "Inactive"}
                        color={isUserActive(user) ? "success" : "default"}
                        variant={isUserActive(user) ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isUserActive(user) ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleToggleActive(user, false)}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => handleToggleActive(user, true)}
                        >
                          Reactivate
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
}
